import React, { useState } from "react";
import { Drawer, Button, Alert, Progress, Typography, message } from "antd";
import {
  ThunderboltOutlined,
  ReloadOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import paths from "nav/paths";
import useAiGenerationUsage from "hooks/usage/useAiGenerationUsage";
import { generateTaskWithAI } from "services/pmService";
import { useAuth } from "contexts/AuthContext";
import { useWorkspace } from "contexts/WorkspaceContext";
import RichTextEditor from "components/common/RichTextEditor";

const { Text, Paragraph } = Typography;

const getRichTextPlainText = (value = "") =>
  String(value)
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .trim();

const sectionView = (label, items) => {
  if (!items || !items.length) return null;
  return (
    <div style={{ marginBottom: 8 }}>
      <Text strong>{label}</Text>
      <ul style={{ margin: "4px 0 0", paddingLeft: 20 }}>
        {items.map((item, index) => (
          <li key={index} style={{ color: "#666", fontSize: 13 }}>{item}</li>
        ))}
      </ul>
    </div>
  );
};

export default function AiTaskDrawer({
  open,
  onClose,
  projectId,
  availableTags = [],
  onUseDraft,
}) {
  const { user } = useAuth();
  const { selectedWorkspaceId } = useWorkspace();
  const navigate = useNavigate();
  const usage = useAiGenerationUsage(selectedWorkspaceId);

  const [prompt, setPrompt] = useState("");
  const [generating, setGenerating] = useState(false);
  const [draft, setDraft] = useState(null);
  const [error, setError] = useState(null);

  const plainPrompt = getRichTextPlainText(prompt);
  const promptLength = plainPrompt.length;
  const hasReachedLimit = usage?.hasReachedLimit ?? false;
  const isPremium = usage?.limit === null;

  const getPlanBadgeStyle = () => {
    if (isPremium) return { bg: "#f3e8ff", border: "#d8b4fe", text: "#7c3aed" };
    if (usage.limit >= 100) return { bg: "#eff6ff", border: "#bfdbfe", text: "#2563eb" };
    return { bg: "#f0fdf4", border: "#bbf7d0", text: "#16a34a" };
  };

  const planName = isPremium ? "Premium" : usage?.limit >= 100 ? "Standard" : "Free";
  const planStyle = getPlanBadgeStyle();

  const handleClose = () => {
    setPrompt("");
    setDraft(null);
    setError(null);
    onClose?.();
  };

  const validatePrompt = () => {
    if (!plainPrompt) {
      message.warning("Please describe the task you want to generate.");
      return false;
    }
    if (plainPrompt.length < 10) {
      message.warning("Please provide at least 10 characters.");
      return false;
    }
    return true;
  };

  const handleGenerate = async () => {
    setError(null);
    if (!validatePrompt()) return;

    setGenerating(true);
    setDraft(null);

    try {
      const result = await generateTaskWithAI(projectId, plainPrompt, undefined, user?.id);

      if (result?.aiGenerated === false && result?.error) {
        setError(result.error);
        message.error(result.error);
        setGenerating(false);
        return;
      }

      setDraft(result);
      message.success("Task draft generated");
      usage.refetch();
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "AI generation is unavailable right now";
      setError(msg);
      message.error("AI generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const handleApply = () => {
    if (!draft) return;

    // Normalize: AI may return arrays for these fields
    const rawDesc = Array.isArray(draft.description)
      ? draft.description.join("\n")
      : (draft.description || "");
    const rawCriteria = Array.isArray(draft.acceptanceCriteria)
      ? draft.acceptanceCriteria.map((c, i) => `${i + 1}. ${c}`).join("\n")
      : (draft.acceptanceCriteria || "");

    // Convert plain text to HTML for Quill editor
    const descHtml = rawDesc
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => `<p>${line}</p>`)
      .join("\n");

    const criteriaLines = rawCriteria
      .split("\n")
      .map((line) => line.replace(/^\d+[\.\)]\s*/, "").trim())
      .filter(Boolean);
    const criteriaHtml = criteriaLines.length > 0
      ? `<ol>${criteriaLines.map((c) => `<li>${c}</li>`).join("")}</ol>`
      : rawCriteria;

    onUseDraft({
      title: draft.title || "",
      description: descHtml,
      acceptanceCriteria: criteriaHtml,
      priority: { CRITICAL: 0, HIGH: 1, MEDIUM: 2, LOW: 3 }[draft.priority] ?? 2,
      dueDate: draft.dueDate || null,
      tagIds: [],
      category: draft.category || "TASK",
      aiGenerated: true,
      aiPrompt: plainPrompt,
      aiModel: "gemini-2.5-flash",
      aiGenerationMetadata: draft.aiGenerationMetadata || null,
    });

    handleClose();
  };

  return (
    <Drawer
      title={
        <span>
          <ThunderboltOutlined style={{ marginRight: 8, color: "#22c55e" }} />
          AI Task Generator
        </span>
      }
      open={open}
      onClose={handleClose}
      width={640}
      footer={null}
      destroyOnClose
    >
      {!draft ? (
        <>
          {/* Limit reached */}
          {hasReachedLimit ? (
            <Alert
              type="warning"
              message={`You've used all ${usage.limit} AI requests this period`}
              description={
                <span>
                  Upgrade to Standard for 100 requests/month or Premium for unlimited AI generations.
                  <br />
                  <Button type="link" onClick={() => navigate(paths.workspaceSubscriptions(selectedWorkspaceId))}>
                    View Plans &rarr;
                  </Button>
                </span>
              }
              showIcon
            />
          ) : (
            <>
              {/* Plan badge + usage + upgrade */}
              {usage && usage.limit !== null && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 10,
                    marginBottom: 12,
                    padding: "12px 14px",
                    background: "#fafbfc",
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 5,
                          padding: "3px 10px",
                          borderRadius: 6,
                          fontSize: 11,
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          background: planStyle.bg,
                          border: `1px solid ${planStyle.border}`,
                          color: planStyle.text,
                        }}
                      >
                        <ThunderboltOutlined style={{ fontSize: 10 }} />
                        {planName}
                      </span>
                      <Text style={{ fontSize: 12, color: "#6b7280" }}>
                        <Text strong style={{ fontSize: 13, color: "#374151" }}>{usage.remaining}</Text>
                        <span style={{ margin: "0 2px" }}>/</span>
                        <Text style={{ color: "#9ca3af" }}>{usage.limit}</Text>
                        <span style={{ marginLeft: 4 }}>requests left</span>
                      </Text>
                    </div>
                    {!isPremium && (
                      <Button
                        type="default"
                        size="small"
                        onClick={() => navigate(paths.workspaceSubscriptions(selectedWorkspaceId))}
                        style={{ fontSize: 12, fontWeight: 600, borderRadius: 6, height: 30 }}
                      >
                        Upgrade
                      </Button>
                    )}
                  </div>
                  <Progress
                    percent={Math.round(((usage.limit - usage.remaining) / usage.limit) * 100)}
                    showInfo={false}
                    size="small"
                    strokeColor={usage.remaining <= 1 ? "#ef4444" : usage.remaining <= 5 ? "#f59e0b" : "#22c55e"}
                    railColor="#e5e7eb"
                  />
                </div>
              )}

              {/* Loading */}
              {usage.loading && usage.limit === null && (
                <div style={{ marginBottom: 12, padding: "8px 12px" }}>
                  <Text type="secondary">Checking AI quota...</Text>
                </div>
              )}

              {/* Prompt input */}
              <div style={{ marginBottom: 12 }}>
                <Paragraph type="secondary" style={{ marginBottom: 8 }}>
                  Describe the task you want to generate. The AI will use the project context to create a structured task draft.
                </Paragraph>

                <RichTextEditor
                  value={prompt}
                  onChange={setPrompt}
                  placeholder='e.g. "Create a task for implementing SSO login with Google"'
                  height="180px"
                  readOnly={generating}
                />
              </div>

              {error && (
                <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} style={{ marginBottom: 12 }} />
              )}

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Text type={promptLength < 10 ? "danger" : "secondary"} style={{ fontSize: 13 }}>
                  {promptLength < 10 ? `Need ${10 - promptLength} more characters` : "Ready"}
                </Text>
                <div style={{ display: "flex", gap: 8 }}>
                  <Button onClick={handleClose} disabled={generating}>Cancel</Button>
                  <Button
                    type="primary"
                    icon={<ThunderboltOutlined />}
                    onClick={handleGenerate}
                    loading={generating}
                    disabled={!plainPrompt || promptLength < 10 || generating || hasReachedLimit}
                    style={!hasReachedLimit ? { backgroundColor: "#22c55e", borderColor: "#22c55e" } : {}}
                  >
                    {generating ? "Generating..." : "Generate"}
                  </Button>
                </div>
              </div>
            </>
          )}
        </>
      ) : (
        /* Draft preview */
        <>
          <Paragraph type="secondary" className="ai-draft-meta-text" style={{ marginBottom: 12 }}>
            AI-generated draft
          </Paragraph>

          {draft.title && (
            <div style={{ marginBottom: 8 }}>
              <Text strong>Title</Text>
              <Paragraph>{draft.title}</Paragraph>
            </div>
          )}

          {draft.summary && (
            <div style={{ marginBottom: 8 }}>
              <Text strong>Summary</Text>
              <Paragraph>{draft.summary}</Paragraph>
            </div>
          )}

          {draft.description && (
            <div style={{ marginBottom: 8 }}>
              <Text strong>Description</Text>
              <Paragraph>{draft.description}</Paragraph>
            </div>
          )}

          {draft.acceptanceCriteria && draft.acceptanceCriteria.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <Text strong>Acceptance Criteria</Text>
              <ul style={{ margin: "4px 0 0", paddingLeft: 20 }}>
                {(Array.isArray(draft.acceptanceCriteria) ? draft.acceptanceCriteria : [draft.acceptanceCriteria]).map((item, i) => (
                  <li key={i} style={{ color: "#666", fontSize: 13 }}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {sectionView("Responsibilities", draft.responsibilities)}
          {sectionView("Requirements", draft.requirements)}
          {sectionView("Preferred Qualifications", draft.preferredQualifications)}
          {sectionView("Benefits", draft.benefits)}
          {sectionView("Required Skills", draft.requiredSkills)}
          {sectionView("Soft Skills", draft.softSkills)}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <Button icon={<ReloadOutlined />} onClick={handleGenerate} loading={generating}>
              Regenerate
            </Button>
            <Button type="primary" icon={<CheckOutlined />} onClick={handleApply}>
              Apply to Form
            </Button>
          </div>
        </>
      )}
    </Drawer>
  );
}
